import DOMPurify from 'dompurify';

/**
 * HTML Sanitization
 *
 * Wraps DOMPurify with an allowlist tuned for the blog preview screen
 * (SPEC.md Phase 1, design point #6: "HTML sanitization on any in-app
 * preview render"). The blog HTML comes from
 * `backend/src/services/blog-html-converter.service.ts` (Google Docs API ->
 * WordPress HTML), which is a trusted server-side converter, but Google
 * Docs content is ultimately user-authored — never render it with
 * `dangerouslySetInnerHTML` without sanitizing first.
 *
 * Allows the tags/attributes the converter is documented to emit: headings,
 * paragraphs, bold/italic/underline, lists, links, and figure/img blocks
 * (for the inline Doc images). No inline event handlers, no <script>/<style>,
 * no `javascript:` URLs.
 */

// Force noopener/noreferrer on any target="_blank" link to prevent
// reverse-tabnabbing (the linked page could otherwise use
// `window.opener` to navigate this tab to a phishing page).
// DOMPurify hooks are global to the module, so guard against adding the
// same hook twice if this module is ever re-evaluated (HMR, tests, etc).
let hooksRegistered = false;
function registerHooks(): void {
  if (hooksRegistered) return;
  hooksRegistered = true;

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}
registerHooks();

const ALLOWED_TAGS = [
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'a',
  'ul',
  'ol',
  'li',
  'br',
  'hr',
  'blockquote',
  'figure',
  'figcaption',
  'img',
  'span',
  'div',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
];

// `class` is intentionally excluded: the converter never emits it, and this
// app is styled with Tailwind utility classes — an injected
// `class="fixed inset-0 z-50 ..."` could render a full-viewport overlay that
// escapes the preview container (UI-redress / phishing risk).
const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'title'];

// `data-aa-image-index` is the ONE exception to "no data-* attrs" (cdb #301
// follow-up): the backend tags each inline `<figure>` with the image's doc
// index (`data-aa-image-index="N"`) so the frontend can live-hide whichever
// figure matches the currently-selected featured image in the blog preview
// (avoids showing that image twice — once as the hero, once in the body).
// It's a plain non-negative integer emitted by our own trusted converter,
// never used as a URL/selector/markup sink — purely an identifier read by
// `hideFigureByImageIndex` below, so it carries no XSS/security surface.
const ADD_ATTR = ['data-aa-image-index'];

// Restrict URI-bearing attributes (href/src) to http(s)/mailto links, plus
// `data:` URIs for raster images only. The Doc-image pipeline uploads to
// S3/CDN and emits normal https `src` URLs — it never needs `data:` — so this
// only closes a hole: `data:image/svg+xml` (and any other data: payload) can
// carry embedded scripts/beacons and must never be allowed through `<img src>`.
const ALLOWED_URI_REGEXP =
  /^(?:(?:https?|mailto):|data:image\/(?:png|jpe?g|gif|webp)(?:;[^,]*)?,|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i;

/**
 * Sanitizes HTML for safe rendering via `dangerouslySetInnerHTML`.
 * Strips scripts, event handlers, and any tag/attribute not on the allowlist.
 */
export function sanitizeHtml(dirtyHtml: string): string {
  return DOMPurify.sanitize(dirtyHtml, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP,
    ADD_ATTR,
  });
}

/**
 * Removes the `<figure data-aa-image-index="indexToHide">` block (if any)
 * from an already-sanitized HTML string — used by the blog preview
 * (`BlogDetail.tsx`) to live-hide the article-body figure matching whichever
 * image is currently selected as the featured/hero image, since the backend
 * now emits ALL images in `htmlBody` (cdb #301 follow-up).
 *
 * Pure and side-effect free (no module-level state, no DOM left behind) —
 * `DOMParser` output is local to this call — so it's safe to unit test
 * headlessly and safe to call on every render.
 */
export function hideFigureByImageIndex(html: string, indexToHide: number | undefined | null): string {
  if (indexToHide === null || indexToHide === undefined) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll(`figure[data-aa-image-index="${indexToHide}"]`).forEach((figure) => figure.remove());
  return doc.body.innerHTML;
}

export default sanitizeHtml;
